import { useEffect, useState } from 'react';
import makeStyles from '@material-ui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import BigCalendar from 'react-big-calendar';
import { useSelector } from 'react-redux';
import moment from 'moment';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import { get } from 'helpers/apiHelpers';
import {
  whiteColor,
  blackColor,
} from 'assets/jss/material-dashboard-pro-react';
import DIET_DAY_BAG_STATUS from 'constants/dietDayBagStatus';
import { alpha } from '@mui/material/styles';

import { dietEvent, nonOperatingEvent } from './calendarEvents';
import CalendarHeader from './CalendarHeader';
import AddDietCalendarTile from './AddDietCalendarTile';
import MonthDayCell from './MonthDayCell';
import WeekdayHeader from './WeekdayHeader';
import getDefaultCalendarMonth from './utils/getDefaultCalendarMonth';
import useIsDayDisabled from './utils/useIsDayDisabled';

const formatDate = 'YYYY-MM-DD';

const useStyles = makeStyles({
  dayLabel: { fontWeight: 400 },
  dayLabelToday: { fontWeight: 600 },
  weekdayLabel: {
    fontSize: '12px',
    fontWeight: 500,
    textTransform: 'uppercase',
  },
  infoBox: {
    lineHeight: 1.2,
    padding: '4px',
    fontSize: '12px',
    fontWeight: 700,
    height: '100%',
    width: '100%',
  },
  infoBoxId: {
    backgroundColor: whiteColor,
    padding: '4px',
    borderRadius: '4px',
    color: blackColor,
    margin: '0 0 16px',
    fontWeight: 600,
  },
  infoBoxLine: { margin: '4px 0' },
  infoBoxAddress: {
    fontWeight: 500,
    fontSize: '14px',
    margin: '8px 0 0',
  },
  infoBoxPickup: {
    fontWeight: 500,
    fontSize: '14px',
    margin: '6px 0 0',
  },
  calendarFade: {
    opacity: 1,
    transition: '0.2s all',
  },
  calendarFadeHidden: {
    opacity: 0,
    transition: '0.2s all',
  },
});

const bagIsDeliveredStatus = [
  DIET_DAY_BAG_STATUS.DELIVERED_NOT_RATED_BLOCKED,
  DIET_DAY_BAG_STATUS.DELIVERED_RATED_BLOCKED,
  DIET_DAY_BAG_STATUS.DELIVERED_RATED_PARTIALLY_BLOCKED,
  DIET_DAY_BAG_STATUS.DELIVERED_NOT_RATED_CAN_RATE,
  DIET_DAY_BAG_STATUS.DELIVERED_RATED_CAN_RATE,
  DIET_DAY_BAG_STATUS.DELIVERED_RATED_PARTIALLY_CAN_RATE,
];

const subscriptionDisabledStatuses = [
  DIET_DAY_BAG_STATUS.SUBSCRIPTION_SUSPENDED,
  DIET_DAY_BAG_STATUS.ERROR_ON_RENEW,
];

const Calendar = ({
  calendarInitDate,
  dietInfo,
  calendarNeedsUpdate,
  setCalendarNeedsUpdate,
  dietId,
  setIsSelectedDaySubscription,
  diets,
  setFetchingDay,
  setBagInfo,
  selectDiet,
  setAddressType,
  bagInfo,
  localizer,
  setFirstVisibleDay,
  setLastVisibleDay,
  fetchingDay,
  firstVisibleDay,
  lastVisibleDay,
}) => {
  const { t } = useTranslation();
  const { user } = useSelector(({ Auth }) => Auth);
  const classes = useStyles();
  const isDayDisabled = useIsDayDisabled();

  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [defaultCalendarMonth, setDefaultCalendarMonth] = useState(null);

  const hasOrderForDay = day => day.id !== null;

  const getAddDietHref = day => {
    const params = new URLSearchParams({
      userId: dietInfo.client?.id ?? '',
      date: moment(day.date).format(formatDate),
      diet: dietInfo.diet?.['@id'] ?? '',
      calorific: dietInfo.calorific?.['@id'] ?? '',
      address: dietInfo.address?.['@id'] ?? '',
      'pick-up-point': dietInfo.pickUpPoint?.['@id'] ?? '',
      'option-change-menu': dietInfo.optionChangeMenu ? '1' : '0',
      'eco-containers': dietInfo.useEcoContainers ? '1' : '0',
    });

    if (dietInfo.variant?.superVariantBeta) {
      (dietInfo.variantMealTypeCombination?.mealTypes ?? []).forEach(
        mealType => {
          params.append('meal-types', mealType['@id']);
        }
      );
    } else {
      params.set('variant', dietInfo.variant?.['@id'] ?? '');
    }

    const url = new URL('/admin/orders/add', window.location.origin);
    url.search = params.toString();

    return url.toString();
  };

  const getCalendarDates = async (firstVisible, lastVisible) => {
    setBagInfo(null);
    try {
      const [ecommerceDiet, employee] = await Promise.all([
        get(
          `/ecommerce-diets/${dietId}/calendar/${firstVisible}/${lastVisible}`
        ),
        get(user.id),
      ]);

      const dietDetails = {
        ...ecommerceDiet,
        days: [...Object.values(ecommerceDiet.days)].map(day => {
          const {
            date,
            newState,
            id,
            diet,
            calorific,
            variant,
            address,
            pickUpPoint,
          } = day;

          if (isDayDisabled(date) && !hasOrderForDay(day)) {
            return nonOperatingEvent(date, t);
          }

          if (
            hasOrderForDay(day) &&
            newState !== DIET_DAY_BAG_STATUS.NOT_DIET_CANT_PLACE_ORDER
          ) {
            const infoBox = (
              <div className={classes.infoBox}>
                <p className={classes.infoBoxId}>{`ID: ${id}`}</p>
                <p className={classes.infoBoxLine}>{diet && diet.name}</p>
                <p className={classes.infoBoxLine}>
                  {calorific && calorific.name}
                </p>
                <p className={classes.infoBoxLine}>{variant && variant.name}</p>

                {address ? (
                  <div>
                    <p className={classes.infoBoxAddress}>
                      {address.street}&nbsp;{address.buildNumber}&nbsp;
                      <br />
                      {address.postCode}&nbsp;
                      <br />
                      {address.city}&nbsp;
                    </p>
                  </div>
                ) : (
                  <div className={classes.infoBoxPickup}>
                    {pickUpPoint && pickUpPoint.value}
                  </div>
                )}
              </div>
            );
            return dietEvent(day, infoBox);
          }

          const isEmployeeLimited = employee.limitationSettingsManagement;
          const shouldAllowModifyPastDays =
            !isEmployeeLimited ||
            newState === DIET_DAY_BAG_STATUS.NOT_DIET_CAN_PLACE_ORDER;

          if (shouldAllowModifyPastDays && dietInfo.diet) {
            return {
              title: <AddDietCalendarTile href={getAddDietHref(day)} />,
              start: new Date(date),
              end: new Date(date),
              height: '120px',
              isEmptyDay: true,
            };
          }

          return null;
        }),
      };
      // Days without a tile (e.g. shop orders, limited employee) are null and must not reach BigCalendar.
      setEvents(dietDetails.days.filter(Boolean));
      setIsLoading(false);
    } catch {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const defaultShownMonth = getDefaultCalendarMonth({
      calendarInitDate,
      firstDeliveryDate: dietInfo.firstDeliveryDate,
      lastDeliveryDate: dietInfo.lastDeliveryDate,
    });

    setDefaultCalendarMonth(defaultShownMonth);

    const startOfDefaultMonth = moment(defaultShownMonth)
      .clone()
      .startOf('month')
      .format(formatDate);

    const endOfDefaultMonth = moment(defaultShownMonth)
      .endOf('month')
      .format(formatDate);

    getCalendarDates(
      moment(startOfDefaultMonth).format(formatDate),
      moment(endOfDefaultMonth).format(formatDate)
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (calendarNeedsUpdate) {
      getCalendarDates(firstVisibleDay, lastVisibleDay);
      setCalendarNeedsUpdate(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarNeedsUpdate, firstVisibleDay, lastVisibleDay]);

  const selectedEvent = async event => {
    const selectedDate = moment(event.start).format(formatDate);
    const isSelectedDaySubscription = event.type === 'SubscriptionIntent';

    if (event.id) {
      setIsSelectedDaySubscription(isSelectedDaySubscription);

      const requestUrl = isSelectedDaySubscription
        ? `/ecommerce-diets/${dietId}/delivery-days/${selectedDate}`
        : event.iri;

      const res = await get(requestUrl);
      const dietObject = diets.find(diet => diet['@id'] === res.diet?.['@id']);
      setFetchingDay(false);
      setBagInfo({ ...res, hasChanged: false });
      selectDiet(dietObject);
      setAddressType(res.address ? 'delivery' : 'pickup');
      return;
    }

    setBagInfo(null);
  };

  // eslint-disable-next-line @typescript-eslint/max-params
  const eventColors = (event, start, end, isSelected) => {
    let style;

    const isSelectedDaySubscription =
      moment(event?.start).format(formatDate) ===
      moment(bagInfo?.date).format(formatDate);

    const { isEmptyDay } = event;
    const isSelectedDayRegularDay = event.iri === bagInfo?.['@id'];

    const eventIsSelected = bagInfo
      ? isSelectedDayRegularDay || isSelectedDaySubscription
      : isSelected;

    const selectedStyle = {
      backgroundColor: '#EEBF00',
      color: '#223649',
      borderRadius: '4px',
      height: event.height || '100%',
    };

    const coloredStyle = {
      backgroundColor: event?.color,
      boxShadow: event.shadow,
      color: event.fontColor,
      borderRadius: '4px',
      height: event.height || '100%',
    };

    const deliveredStyle = event?.color
      ? {
          backgroundColor: alpha(event.color, 0.5),
        }
      : { backgroundColor: '#627381' };

    const disabledStyle = {
      opacity: 0.5,
    };

    const defaultStyle = {
      backgroundColor: '#627381',
      color: whiteColor,
    };

    const emptyDayStyle = {
      backgroundColor: whiteColor,
      height: event.height,
      cursor: 'pointer',
    };

    if (isEmptyDay) {
      style = emptyDayStyle;
    } else if (eventIsSelected) {
      style = selectedStyle;
    } else if (event?.color) {
      style = coloredStyle;
    } else {
      style = defaultStyle;
    }

    const isDelivered = bagIsDeliveredStatus.includes(event.newState);
    const isSubscriptionDisabled = subscriptionDisabledStatuses.includes(
      event.newState
    );

    if (isDelivered && !eventIsSelected) {
      style = { ...style, ...deliveredStyle };
    }

    if (isSubscriptionDisabled) {
      style = { ...style, ...disabledStyle };
    }

    const eventClassName =
      event?.variant === 'no-diet'
        ? 'rbc-event--no-diet'
        : 'rbc-event--content';

    return {
      style,
      className: eventClassName,
    };
  };

  return (
    <>
      {defaultCalendarMonth && (
        <div>
          <Card>
            <CardBody calendar={true}>
              <div
                className={
                  isLoading ? classes.calendarFadeHidden : classes.calendarFade
                }
              >
                <BigCalendar
                  localizer={localizer}
                  components={{
                    toolbar: props => <CalendarHeader {...props} />,
                    month: { dateHeader: MonthDayCell, header: WeekdayHeader },
                  }}
                  events={events}
                  onRangeChange={view => {
                    const firstVisibleDate = moment(view.start).format(
                      formatDate
                    );
                    const lastVisibleDate = moment(view.end).format(formatDate);

                    setFirstVisibleDay(firstVisibleDate);
                    setLastVisibleDay(lastVisibleDate);
                    getCalendarDates(firstVisibleDate, lastVisibleDate);
                  }}
                  views={['month']}
                  defaultDate={defaultCalendarMonth}
                  tooltipAccessor={() => null}
                  onSelectEvent={event => !fetchingDay && selectedEvent(event)}
                  eventPropGetter={eventColors}
                  formats={{
                    dateFormat: 'D',
                    dayFormat: (date, culture, formatLocalizer) =>
                      formatLocalizer
                        .format(date, 'ddd', culture)
                        .toUpperCase(),
                    monthHeaderFormat: (date, culture, formatLocalizer) =>
                      formatLocalizer.format(date, 'MMMM YYYY', culture),
                  }}
                />
              </div>
            </CardBody>
          </Card>
        </div>
      )}
    </>
  );
};

export default Calendar;
